import otapi, { DiagConsoleLogger, DiagLogLevel, Tracer } from '@opentelemetry/api';
import { Resource, ResourceAttributes } from '@opentelemetry/resources';
import { ConsoleSpanExporter, SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import {
  InstrumentationOption,
  registerInstrumentations,
} from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { SpanProcessor } from '@opentelemetry/sdk-trace-base/build/src/SpanProcessor';
import NitroFetchInstrumentation from './instrumentation/NitroFetchInstrumentation';

type InstrumentationFactory = () => InstrumentationOption;

/**
 * Standard instrumentation instances. Available statically so they can be registered and
 * unregistered as needed.
 *
 * Note that these are not direct instance instantiations but factories (anonymous functions) that
 * produce an instrumentation instance. This is important, as InstrumentationBase applies the
 * instrumentation _in the constructor_, so immediately when the instrumentation is instantiated.
 * This prevents them from being unregistered or having their order reasonably controlled.
 *
 * The factories are still expected to have this behavior, as they are instantiating an instance,
 * but the factories are called immediately before start(), in the order they're registered.
 *
 * See https://opentelemetry.io/docs/instrumentation/js/instrumentation/
 */
export const Instrumentations = {
  DocumentLoad: () => new DocumentLoadInstrumentation() as InstrumentationOption,
  Fetch: () => new NitroFetchInstrumentation(),
};

export const SpanProcessors = {
  Console: new SimpleSpanProcessor(new ConsoleSpanExporter()),
};

export interface OpenTelemetryManagerOptions {
  appName: string;
  appVersion?: string;
}

function resourceAttributes(options: OpenTelemetryManagerOptions) {
  const attributes: ResourceAttributes = {
    [SemanticResourceAttributes.SERVICE_NAME]: options.appName,
  };
  if (options.appVersion) {
    attributes[SemanticResourceAttributes.SERVICE_VERSION] = options.appVersion;
  }
  return attributes;
}

/**
 * A construct to manage both standard configuration for OpenTelemetry as well as lifecycle hooks,
 * as the OpenTelemetry sdk startup is asynchronous.
 */
export default class OpenTelemetryManager {
  private readonly instrumentationFactories: InstrumentationFactory[];
  private readonly resourceAttributes: ResourceAttributes;
  private readonly spanProcessors: SpanProcessor[] = [];
  private resource: Resource | undefined;
  private traceProvider: WebTracerProvider | undefined;

  constructor(options: OpenTelemetryManagerOptions) {
    this.instrumentationFactories = [
      Instrumentations.DocumentLoad,
      Instrumentations.Fetch,
    ];
    this.resourceAttributes = resourceAttributes(options);
  }

  private newProvider() {
    const provider = new WebTracerProvider({
      resource: this.newResource(),
    });
    provider.register({
      // Changing default contextManager to use ZoneContextManager to support asynchronous operations
      contextManager: new ZoneContextManager(),
    });
    // Span Processors
    this.spanProcessors.forEach((p) => provider.addSpanProcessor(p));
    // Instrumentations
    registerInstrumentations({
      instrumentations: this.instrumentationFactories.map((f) => f()),
    });
    return provider;
  }

  private newResource() {
    if (this.resource) {
      return this.resource;
    }
    return new Resource(this.resourceAttributes);
  }

  /* Configuration */

  /**
   * Enable writing spans to the console. This is not expected to be used in a real scenario but
   * can be useful for debugging.
   */
  withConsoleTraceExporter(): OpenTelemetryManager {
    return this.withSpanProcessor(SpanProcessors.Console);
  }

  /**
   * Turn on debug logging from OpenTelemetry's internal logger.
   */
  withDebugLogs(): OpenTelemetryManager {
    otapi.diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.ALL);
    return this;
  }

  /**
   * Add an instrumentation to the OpenTelemetry config. The instrumentation is expected to be
   * returned from an anonymous function (a factory) to allow control of applying it.
   *
   * @param instrumentationFactory
   */
  withInstrumentation(
    instrumentationFactory: InstrumentationFactory,
  ): OpenTelemetryManager {
    this.instrumentationFactories.push(instrumentationFactory);
    return this;
  }

  /**
   * Set the OpenTelemetry resource explicitly. Doing this overrides any current attributes and makes
   * withResourceAttributes() unusable, as the resource is immutable.
   *
   * @param resource
   */
  withResource(resource: Resource): OpenTelemetryManager {
    this.resource = resource;
    return this;
  }

  /**
   * Add or override attributes at the resource level..
   *
   * @param attributes
   */
  withResourceAttributes(attributes: ResourceAttributes): OpenTelemetryManager {
    if (this.resource) {
      throw new Error(
        'resource has been overridden explicitly, set attributes on it instead of using withResourceAttributes()',
      );
    }
    Object.assign(this.resourceAttributes, attributes);
    return this;
  }

  /**
   * Remove a configured OpenTelemetry instrumentation, preventing it from being applied on start.
   * The construct being removed is the factory that returns the instrumentation and must be the
   * same one that was registered initially.
   *
   * @param instrumentationFactory
   */
  withoutInstrumentation(
    instrumentationFactory: InstrumentationFactory,
  ): OpenTelemetryManager {
    const index = this.instrumentationFactories.indexOf(instrumentationFactory);
    if (index >= 0) {
      this.instrumentationFactories.splice(index, 1);
    }
    return this;
  }

  withSpanProcessor(spanProcessor: SpanProcessor): OpenTelemetryManager {
    this.spanProcessors.push(spanProcessor);
    return this;
  }

  /* Lifecycle */

  newTracer(name: string): Tracer {
    if (!this.traceProvider) {
      throw new Error(
        'OpenTelemetry trace provider has not been instantiated. Has OpenTelemetryManager.start() been called?',
      );
    }
    return this.traceProvider.getTracer(name);
  }

  /**
   * Start the OpenTelemetry trace provider with the current configuration.
   */
  start(): OpenTelemetryManager {
    this.traceProvider = this.newProvider();
    otapi.diag.debug('OpenTelemetryManager started.');
    return this;
  }
}
